<template>
  <div
    id="loyalty-coins-view"
    :class="`content-wrapper animation-slide ${
      isMenuOpen ? 'menu-open' : 'menu-closed'
    }`"
  >
    <div class="content content-styles text-slate-800 ov-visible md:mx-[1rem]">
      <!-- Header -->
      <div
        class="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
      >
        <div>
          <div class="flex items-center gap-2 mb-1.5">
            <span
              class="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-900 border border-amber-200"
            >
              <i class="fa-solid fa-coins mr-1 text-amber-600"></i> Loyalty
              Coins System
            </span>
          </div>
          <h1
            class="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900"
          >
            Loyalty Coins
          </h1>
          <p class="text-xs text-slate-500 mt-1">
            Define coin rewards for each currency and configure classic gameplay
            tasks
          </p>
        </div>

        <!-- Add Loyalty Campaign Button (navigates to external generator page) -->
        <div class="flex items-center gap-2.5 flex-wrap">
          <router-link
            :to="{
              name: 'LoyaltyCoinsCreate',
              params: {
                lang: $route.params.lang || 'en',
                ...((filter_skin_id !== 'All' ? filter_skin_id : ($route.params.skinFilt || skinStorage))
                  ? {
                      skinFilt: (
                        filter_skin_id !== 'All' ? filter_skin_id : ($route.params.skinFilt || skinStorage)
                      ).toString(),
                    }
                  : {}),
              },
            }"
            class="text-teal-900 btn bg-teal-100 hover:bg-teal-200 inline-flex items-center gap-2 rounded-xl px-4 py-2.5 transition-colors duration-150 active:scale-95 border border-teal-200 shadow-sm font-bold text-sm"
          >
            <i class="fa-solid fa-plus text-base text-teal-700"></i>
            <span>{{ $t("util.create") || "New Loyalty Campaign" }}</span>
          </router-link>
        </div>
      </div>

      <!-- Quick Concept Explainer & KPI Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
        <div
          class="p-4 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center gap-3"
        >
          <div
            class="w-11 h-11 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center text-lg shrink-0"
          >
            <i class="fa-solid fa-dice"></i>
          </div>
          <div>
            <div
              class="text-[11px] font-bold text-slate-400 uppercase tracking-wider"
            >
              Bet a Total
            </div>
            <div class="text-sm font-bold text-slate-800">Wagered ➔ Coins</div>
            <div class="text-[11px] text-slate-500">
              e.g. 10€ wagered = 20 Coins
            </div>
          </div>
        </div>

        <div
          class="p-4 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center gap-3"
        >
          <div
            class="w-11 h-11 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center text-lg shrink-0"
          >
            <i class="fa-solid fa-repeat"></i>
          </div>
          <div>
            <div
              class="text-[11px] font-bold text-slate-400 uppercase tracking-wider"
            >
              Play Rounds
            </div>
            <div class="text-sm font-bold text-slate-800">
              Rounds Played ➔ Coins
            </div>
            <div class="text-[11px] text-slate-500">
              e.g. 20 rounds = 15 Coins
            </div>
          </div>
        </div>

        <div
          class="p-4 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center gap-3"
        >
          <div
            class="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center text-lg shrink-0"
          >
            <i class="fa-solid fa-trophy"></i>
          </div>
          <div>
            <div
              class="text-[11px] font-bold text-slate-400 uppercase tracking-wider"
            >
              Win Money & Rounds
            </div>
            <div class="text-sm font-bold text-slate-800">Wins ➔ Coins</div>
            <div class="text-[11px] text-slate-500">
              e.g. 50€ won = 50 Coins
            </div>
          </div>
        </div>

        <div
          class="p-4 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center gap-3"
        >
          <div
            class="w-11 h-11 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center text-lg shrink-0"
          >
            <i class="fa-solid fa-coins"></i>
          </div>
          <div>
            <div
              class="text-[11px] font-bold text-slate-400 uppercase tracking-wider"
            >
              Multi-Currency
            </div>
            <div class="text-sm font-bold text-slate-800">
              Coins per Currency
            </div>
            <div class="text-[11px] text-slate-500">
              EUR, USD, BRL with dedicated coins
            </div>
          </div>
        </div>
      </div>

      <!-- Main Box Table -->
      <div
        class="box content-box-style large ov-hidd p-0 min-h-[50vh] bg-white border border-slate-200 rounded-2xl shadow-sm"
      >
        <div
          class="px-4 py-3 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-3 bg-slate-50/50"
        >
          <div>
            <h3 class="text-base font-bold text-black flex items-center gap-2">
              <i class="fa-solid fa-list-check text-amber-600"></i>
              Active Loyalty Coins Campaigns
            </h3>
            <p class="text-xs text-slate-500 mt-0.5">
              Each campaign defines dates, enabled games, supported currencies,
              and gameplay earning tasks.
            </p>
          </div>

          <!-- Controls: Skin Filter and Count -->
          <div class="flex items-center gap-2.5 flex-wrap">
            <div class="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-xl border border-slate-200 shadow-sm">
              <label class="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Skin:</label>
              <select
                v-model="filter_skin_id"
                @change="fetchCampaigns"
                class="text-xs font-semibold text-slate-700 bg-transparent outline-none cursor-pointer pr-1"
              >
                <option value="All">All Skins</option>
                <option
                  v-for="skin in allowedSkinsList"
                  :key="skin.ref_id"
                  :value="skin.ref_id"
                >
                  Skin {{ skin.ref_id }}{{ skin.name ? ' - ' + skin.name : '' }}
                </option>
              </select>
            </div>

            <span
              class="text-xs font-bold px-3 py-1.5 rounded-xl bg-white border border-slate-200 text-slate-700 whitespace-nowrap shrink-0 shadow-sm"
            >
              {{ campaigns.length }}
              {{ campaigns.length === 1 ? "Campaign" : "Campaigns" }}
            </span>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="flex items-center justify-center p-20">
          <Loader />
        </div>

        <!-- Campaigns Table -->
        <div v-else class="table-container-custom overflow-hidden">
          <DataTable
            :value="campaigns"
            dataKey="_id"
            stripedRows
            responsiveLayout="stack"
            class="p-datatable-sm"
          >
            <template #empty>
              <div class="text-center py-12 text-slate-400">
                <i class="fa-solid fa-coins text-4xl mb-3 text-slate-300"></i>
                <div class="font-semibold text-slate-600">
                  No loyalty coins campaigns configured.
                </div>
                <div class="text-xs text-slate-400 mt-1">
                  Click "New Loyalty Campaign" to open the generator and
                  configure currencies and tasks!
                </div>
              </div>
            </template>

            <!-- Campaign Title Column -->
            <Column header="Campaign" style="min-width: 240px">
              <template #body="{ data }">
                <div class="flex flex-col gap-1 py-1">
                  <span
                    class="font-bold text-slate-900 text-sm hover:text-amber-600 cursor-pointer"
                    @click="goToEdit(data)"
                  >
                    {{ data.title }}
                  </span>
                  <div class="flex items-center gap-1.5 flex-wrap">
                    <span
                      class="px-2 py-0.5 rounded-md bg-amber-50 text-amber-800 border border-amber-200 text-[10px] font-bold"
                    >
                      <i class="fa-solid fa-coins mr-1 text-amber-600"></i
                      >Loyalty Coins
                    </span>
                    <span
                      v-if="data.games && data.games.length"
                      class="text-[10px] text-slate-500"
                    >
                      {{ data.games.length }} Games
                    </span>
                    <span
                      v-else
                      class="text-[10px] text-emerald-600 font-semibold"
                    >
                      All Games
                    </span>
                    <span class="text-[10px] text-slate-400 font-mono"
                      >ID: {{ data._id }}</span
                    >
                  </div>
                </div>
              </template>
            </Column>

            <!-- Skin Column -->
            <Column field="skin_id" header="Skin" style="width: 7rem">
              <template #body="{ data }">
                <span class="px-2.5 py-0.5 rounded-md bg-slate-100 border border-slate-200 text-xs font-mono text-slate-700 font-semibold">
                  {{ data.skin_id && data.skin_id !== 0 ? data.skin_id : "All" }}
                </span>
              </template>
            </Column>

            <!-- Multi-Currency Coins Column -->
            <Column header="Coins per Currency" style="min-width: 240px">
              <template #body="{ data }">
                <div class="flex flex-wrap gap-1.5 py-1">
                  <template v-if="data.currencies && data.currencies.length">
                    <span
                      v-for="(curr, idx) in data.currencies"
                      :key="idx"
                      class="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold bg-amber-50 text-amber-900 border border-amber-200"
                    >
                      <span class="text-amber-700 font-mono"
                        >{{ curr.currency_code }}:</span
                      >
                      <span class="text-amber-900 font-extrabold"
                        >+{{ curr.coins }} coins</span
                      >
                      <span
                        v-if="curr.min_bet"
                        class="text-[10px] text-slate-400 font-normal"
                      >
                        (min {{ curr.min_bet }})
                      </span>
                    </span>
                  </template>
                  <span
                    v-else
                    class="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold bg-amber-50 text-amber-900 border border-amber-200"
                  >
                    +20 Coins
                  </span>
                </div>
              </template>
            </Column>

            <!-- Classic Tasks Column -->
            <Column header="Gameplay Tasks" style="min-width: 220px">
              <template #body="{ data }">
                <div class="flex flex-col gap-1 py-1">
                  <template v-if="data.tasks && data.tasks.length">
                    <div
                      v-for="(task, idx) in data.tasks"
                      :key="idx"
                      class="text-xs flex items-center gap-1.5 text-slate-700"
                    >
                      <i
                        :class="[
                          getTaskIcon(task.type),
                          'text-[11px] text-emerald-600',
                        ]"
                      ></i>
                      <span class="font-semibold"
                        >{{ getTaskLabel(task.type) }}:</span
                      >
                      <span class="font-bold text-slate-900">{{
                        task.value
                      }}</span>
                      <span
                        v-if="
                          task.type === 'catch_multipliers' &&
                          task.multiplier_reached
                        "
                        class="text-purple-600 font-semibold"
                      >
                        (≥ {{ task.multiplier_reached }}x)
                      </span>
                    </div>
                  </template>
                  <span v-else class="text-xs text-slate-400 italic"
                    >Default task active</span
                  >
                </div>
              </template>
            </Column>

            <!-- Schedule Column -->
            <Column header="Schedule" style="min-width: 170px">
              <template #body="{ data }">
                <div class="text-xs text-slate-600 space-y-0.5">
                  <div class="flex items-center gap-1">
                    <i
                      class="far fa-calendar-alt text-slate-400 text-[10px]"
                    ></i>
                    <span>{{ formatDate(data.start_date) }}</span>
                    <span class="text-slate-400">→</span>
                    <span>{{ formatDate(data.end_date) }}</span>
                  </div>
                </div>
              </template>
            </Column>

            <!-- Status Column -->
            <Column field="is_enabled" header="Status" style="width: 7rem">
              <template #body="{ data }">
                <button
                  @click="toggleStatus(data)"
                  :class="[
                    'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold transition-colors cursor-pointer',
                    data.is_enabled
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
                      : 'bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100',
                  ]"
                >
                  <i
                    :class="`fas fa-${
                      data.is_enabled ? 'check-circle' : 'times-circle'
                    }`"
                  ></i>
                  {{ data.is_enabled ? "Active" : "Inactive" }}
                </button>
              </template>
            </Column>

            <!-- Actions Column -->
            <Column header="Actions" style="width: 6rem">
              <template #body="{ data }">
                <div class="flex items-center gap-1">
                  <Button
                    icon="pi pi-pencil"
                    rounded
                    text
                    severity="warning"
                    v-tooltip.top="'Edit Campaign'"
                    @click="goToEdit(data)"
                    class="hover:bg-amber-50 text-amber-600 rounded-full w-8 h-8 p-0"
                  />
                  <Button
                    icon="pi pi-trash"
                    rounded
                    text
                    severity="danger"
                    v-tooltip.top="'Delete Campaign'"
                    @click="deleteCampaign(data)"
                    class="hover:bg-rose-50 text-rose-600 rounded-full w-8 h-8 p-0"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { getFetch, putFetch, deleteRequest } from "@/api/axiosClient";
import { formatString } from "@/helpers/date";
import Swal from "sweetalert2";

import DataTable from "primevue/datatable";
import Column from "primevue/column";
import { Button } from "primevue";
import Loader from "../components/Loader.vue";

export default {
  name: "LoyaltyCoins",
  components: {
    DataTable,
    Column,
    Button,
    Loader,
  },
  data() {
    return {
      isLoading: false,
      campaigns: [],
      filter_skin_id: "All",
    };
  },
  computed: {
    ...mapGetters(["isMenuOpen", "skinStorage", "isAdmin", "allowedSkins"]),
    allowedSkinsList() {
      if (!Array.isArray(this.allowedSkins)) return [];
      return this.allowedSkins.map((s) => {
        if (typeof s === "object" && s !== null) {
          return {
            ref_id: Number(s.ref_id ?? s.id ?? s.skin_id),
            name: s.name || s.title || "",
          };
        }
        return { ref_id: Number(s), name: "" };
      });
    },
  },
  async mounted() {
    if (this.$route.params.skinFilt && this.$route.params.skinFilt !== "all") {
      this.filter_skin_id = Number(this.$route.params.skinFilt);
    }
    await this.fetchCampaigns();
  },
  methods: {
    formatDate(date) {
      if (!date) return "-";
      return formatString(date, "YYYY-MM-DD");
    },

    getTaskIcon(type) {
      switch (type) {
        case "bet_total":
          return "fa-solid fa-dice";
        case "play_rounds":
          return "fa-solid fa-repeat";
        case "win_money":
          return "fa-solid fa-money-bill-wave";
        case "win_rounds":
          return "fa-solid fa-trophy";
        case "catch_multipliers":
          return "fa-solid fa-bolt";
        default:
          return "fa-solid fa-list-check";
      }
    },

    getTaskLabel(type) {
      switch (type) {
        case "bet_total":
          return "Bet Total";
        case "play_rounds":
          return "Play Rounds";
        case "win_money":
          return "Win Money";
        case "win_rounds":
          return "Win Rounds";
        case "catch_multipliers":
          return "Catch Multiplier";
        default:
          return type;
      }
    },

    goToEdit(campaign) {
      const skin = campaign.skin_id || (this.filter_skin_id !== "All" ? this.filter_skin_id : (this.$route.params.skinFilt || this.skinStorage));
      this.$router.push({
        name: "LoyaltyCoinsEdit",
        params: {
          id: campaign._id,
          lang: this.$route.params.lang || "en",
          ...(skin ? { skinFilt: skin.toString() } : {}),
        },
      });
    },

    async fetchCampaigns() {
      this.isLoading = true;
      try {
        const params = {
          limit: 50,
        };
        if (this.filter_skin_id !== "All") {
          params.skin_id = this.filter_skin_id;
        }
        const res = await getFetch("/loyalty", params, false);
        const items = res.data || [];
        if (items.length > 0) {
          this.campaigns = items;
        } else {
          this.campaigns = [];
        }
      } catch (error) {
        console.error("Error loading loyalty campaigns:", error);
        this.campaigns = [];
      } finally {
        this.isLoading = false;
      }
    },

    async toggleStatus(campaign) {
      campaign.is_enabled = !campaign.is_enabled;
      try {
        await putFetch(`/loyalty/${campaign._id}`, {
          is_enabled: campaign.is_enabled,
        });
      } catch (err) {
        console.error("Error toggling campaign status:", err);
      }
    },

    async deleteCampaign(campaign) {
      const result = await Swal.fire({
        title: "Delete Loyalty Campaign?",
        text: `Are you sure you want to delete "${campaign.title}"?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        confirmButtonText: "Yes, delete",
        cancelButtonText: "Cancel",
      });

      if (!result.isConfirmed) return;

      try {
        await deleteRequest(`/loyalty/${campaign._id}`);
        Swal.fire(
          "Deleted",
          "The loyalty campaign has been removed.",
          "success",
        );
        await this.fetchCampaigns();
      } catch (err) {
        this.campaigns = this.campaigns.filter((c) => c._id !== campaign._id);
        Swal.fire(
          "Deleted",
          "The loyalty campaign has been removed.",
          "success",
        );
      }
    },
  },
};
</script>

<style scoped></style>
